import { stubTrue } from "lodash";

import { DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { TableType } from "app/components/widget/widget-types/TableKeywordAnalysisDashboardWidget";
import { translate } from "app/filters/i18nFilter";
import { serpRowsConfigs } from "app/common/keyword-research/keywords-overview/serpMetricsConsts";
import { ESerpMetric } from "app/common/keyword-research/keywords-overview/serpMetricsTypes";
import { voulmeClicksRowsConfig } from "app/common/keyword-research/keywords-overview/volumeAndClicksConsts";
import { EVolumeAndClicksMetric } from "app/common/keyword-research/keywords-overview/volumeAndClicksTypes";
import { BasicDurations } from "app/services/DurationService";
import type { EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { DefaultCellRightAlign } from "../../../React/Table/cells/DefaultCellRightAlign";

import type { IBaseConfig, IWidgetConfig } from "./@types/IMetric";

const createConfig = (widgetConfig: IWidgetConfig, baseConfig: IBaseConfig) => {
    const { id, title, endpoint, state, tableType, categoriesEndpoint, domainsEndpoint } = widgetConfig;
    return {
        id,
        ...baseConfig,
        properties: {
            ...baseConfig.properties,
            title,
            endpoint,
            state,
            tableType,
            categoriesEndpoint,
            domainsEndpoint,
        },
    };
};

export const KeywordAnalysisTrendOverTime = {
    id: "KeywordAnalysisTrendOverTime",
    properties: {
        dashboard: "true",
        family: "Keyword",
        component: "KeywordAnalysisV2Overview",
        mobileWebComponent: "KeywordAnalysisV2Overview",
        availabilityComponent: "DigitalSuiteAcquisition",
        state: "organicsearch_keywordAnalysis_overview_2",
        title: "keywordAnalysis.trend_over_time.dashboard.widget.title",
        apiParams: {
            timeGranularity: GranularityNames.WEEKLY,
        },
        viewPermissions: {
            trafficSources: [devicesTypes.TOTAL, devicesTypes.DESKTOP, devicesTypes.MOBILE],
        },
        hasWebSource: true,
        defaultDuration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
    },
    widgets: {
        single: {
            defaultType: "KeywordAnalysisTrendOverTimeGraph",
            KeywordAnalysisTrendOverTimeGraph: {
                properties: {
                    dashboardSubtitleMarginBottom: 15,
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                },
                y_axis: {
                    title: "Clicks & Volume",
                    name: "Clicks & Volume",
                    format: "number",
                    type: "long",
                },
            },
        },
    },
};

/**
 * @deprecated KeywordAnalysisCPC --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisCPC = {
    id: "KeywordAnalysisCPC",
    properties: {
        component: "KeywordAnalysisOPTotal",
        family: "Keyword",
        dashboard: "true",
        title: "KeywordAnalysis.organic.widgets.cpc",
        webSource: "Desktop",
        subtitle: "KeywordAnalysis.organic.cpc.subtitle",
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsPaidKeywords",
        isDeprecated: true,
        isHidden: stubTrue,
    },
    widgets: {
        single: {
            SingleMetric: {
                properties: {
                    headTemp: "",
                    options: {
                        showLegend: false,
                        showSettings: true,
                        showFrame: true,
                        showOverflow: true,
                        template: "/app/pages/keyword-analysis/templates/cpc-dashboard.html",
                    },
                },
                objects: {
                    TotalVisits: {
                        name: "CPC",
                        title: "",
                        type: "double",
                        format: "abbrNumber",
                        cellTemp: "large-number",
                        ppt: {
                            overrideFormat: "cpc",
                        },
                    },
                },
            },
        },
        compare: {},
    },
};

const TrafficBreakdownBaseConfig: IBaseConfig = {
    properties: {
        dashboard: "true",
        family: "Keyword",
        hasWebSource: true,
        component: "KeywordAnalysisV2Organic",
        order: "1",
        customFetcherFactory: {
            factoryName: "KeywordAnalysisDataFetcherFactory",
            overrideDashboardFetching: true,
        },
        viewPermissions: {
            trafficSources: [devicesTypes.TOTAL, devicesTypes.DESKTOP, devicesTypes.MOBILE] as EDeviceType[],
        },
        mobileWebComponent: "KeywordAnalysisV2Organic",
        availabilityComponent: "DigitalSuiteAcquisition",
        defaultDuration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
        showLastOneMonth: true, //if the selected month is the current month, show last 1 month
    },
    widgets: {
        single: {
            defaultType: "TableKeywordAnalysisDashboard",
            TableKeywordAnalysisDashboard: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                        dashboardSubtitleClass: "dashboard-table-widget",
                    },
                },
                columns: [],
            },
        },
        compare: {
            defaultType: "TableKeywordAnalysisDashboard",
            TableKeywordAnalysisDashboard: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {},
                },
                columns: [],
            },
        },
    },
};

export const TrafficBreakdown = createConfig(
    {
        id: "TrafficBreakdown",
        title: "dashboard.metricGallery.Keyword.TableKeywordAnalysisDashboard.title",
        endpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/Table",
        domainsEndpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/Domains",
        categoriesEndpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/Categories",
        state: "organicsearch_keywordAnalysis_2",
        tableType: TableType.Domains,
    },
    TrafficBreakdownBaseConfig,
) as IBaseConfig;
export const UrlTrafficBreakdown = createConfig(
    {
        id: "UrlTrafficBreakdown",
        title: "dashboard.metricGallery.Keyword.UrlTrafficBreakdown.title",
        endpoint: "api/KeywordAnalysis/Pages/Table",
        domainsEndpoint: "api/KeywordAnalysis/Pages/Domains",
        categoriesEndpoint: "api/KeywordAnalysis/Pages/Categories",
        state: "organicsearch_trafficAnalysis_2",
        tableType: TableType.Urls,
    },
    TrafficBreakdownBaseConfig,
) as IBaseConfig;

const clicksOverTimeBaseConfig: IBaseConfig = {
    properties: {
        dashboard: "true",
        family: "Keyword",
        hasWebSource: true,
        component: "KeywordAnalysisV2Organic",
        order: "1",
        apiParams: {
            timeGranularity: GranularityNames.WEEKLY,
        },
        viewPermissions: {
            trafficSources: [devicesTypes.TOTAL, devicesTypes.DESKTOP, devicesTypes.MOBILE] as EDeviceType[],
        },
        mobileWebComponent: "KeywordAnalysisV2Organic",
        availabilityComponent: "DigitalSuiteAcquisition",
        defaultDuration: BasicDurations.LAST_TWENTY_EIGHT_DAYS,
        showLastOneMonth: true, //if the selected month is the current month, show last 1 month
    },
    widgets: {
        single: {
            defaultType: "KeywordAnalysisGraphDashboard",
            KeywordAnalysisGraphDashboard: {
                properties: {
                    dashboardSubtitleMarginBottom: 15,
                    hideMarkersOnDaily: true,
                },
                x_axis: {
                    title: "dashboard.widget.clicks.over.time.xaxis.title",
                    type: "date",
                    format: "None",
                    name: "dashboard.widget.clicks.over.time.xaxis.title",
                },
                y_axis: {
                    title: "dashboard.widget.clicks.over.time.yaxis.title",
                    type: "long",
                    format: "number",
                    formatParameter: 0,
                    name: "dashboard.widget.clicks.over.time.yaxis.title",
                },
            },
        },
        compare: {
            defaultType: "KeywordAnalysisGraphDashboard",
            KeywordAnalysisGraphDashboard: {
                properties: {
                    hideMarkersOnDaily: true,
                    options: {
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                legend: {
                    enabled: false,
                },
            },
        },
    },
};

export const ClicksOverTime = createConfig(
    {
        id: "ClicksOverTime",
        title: "dashboard.metricGallery.Keyword.ClicksOverTime.title",
        endpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/SiteTraffic",
        domainsEndpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/Domains",
        categoriesEndpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/Categories",
        state: "organicsearch_keywordAnalysis_2",
    },
    clicksOverTimeBaseConfig,
) as IBaseConfig;

export const UrlClicksOverTime = createConfig(
    {
        id: "UrlClicksOverTime",
        title: "dashboard.metricGallery.Keyword.UrlClicksOverTime.title",
        endpoint: "api/KeywordAnalysis/Pages/Trend",
        domainsEndpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/Domains",
        categoriesEndpoint: "widgetApi/KeywordAnalysisV2/KeywordAnalysisOrganic/Categories",
        state: "organicsearch_trafficAnalysis_2",
    },
    clicksOverTimeBaseConfig,
) as IBaseConfig;

/**
 * @deprecated KeywordAnalysisOrganic --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisOrganic = {
    id: "KeywordAnalysisOrganic",
    properties: {
        component: "KeywordAnalysisOP",
        family: "Keyword",
        dashboard: "true",
        title: "keywordAnalysis.organic.traffic.title",
        viewPermissions: {
            trafficSources: ["MobileWeb", "Desktop"],
        },
        hasWebSource: true,
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsOverviewKeywords",
        isHidden: stubTrue,
        isDeprecated: true,
    },
    widgets: {
        single: {
            KeywordsTable: {
                properties: {},
                columns: [
                    {
                        fixed: true,
                        name: "row-selection",
                        cellTemp: "row-selection",
                        sortable: false,
                        width: 33,
                        disableHeaderCellHover: true,
                    },
                    {
                        fixed: true,
                        cellTemp: "index",
                        sortable: false,
                        width: 65,
                        disableHeaderCellHover: true,
                    },
                    {
                        name: "Domain",
                        fixed: true,
                        title: "Domain",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "keyword-analysis-traffic-source",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        width: 220,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        width: 200,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        width: 110,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },
                    {
                        name: "Position",
                        title: "Position",
                        type: "int",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "asc",
                        groupable: "False",
                        cellTemp: "keyword-analysis-position",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        width: 80,
                    },
                    {
                        name: "Url",
                        title: "Destination URL",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "keyword-analysis-url",
                        cellCls: "url-cell",
                        cellClass: "url-cell",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        width: 275,
                    },
                    {
                        name: "Category",
                        title: "dropdown.category",
                        type: "string",
                        format: "prettifyCategory",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "default-cell",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        width: 220,
                    },
                    {
                        name: `Serp`,
                        trackingName: `Serp`,
                        title: "analysis.source.search.all.table.columns.featured.title",
                        tooltip: "analysis.source.search.all.table.columns.featured.title.tooltip",
                        cellTemp: "adsense-cell",
                        sortable: true,
                        width: 95,
                    },
                ],
                filters: {},
            },
            KeywordsDashboardTable: {
                properties: {
                    apiParams: {
                        pageSize: "10",
                    },
                    showMoreButtonItems: 5,
                    options: {
                        showFrame: true,
                        showLegend: false,
                        showOverflow: true,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                columns: [
                    {
                        name: "Domain",
                        fixed: true,
                        title: "Domain",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "website-tooltip-top-no-expand-cell",
                        headTemp: "grouped-header-cell",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 220,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "False",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share-dashboard",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        width: "115px",
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "Change-percentage-dashboard",
                        headerComponent: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 110,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },
                    {
                        name: "Position",
                        title: "Pos.",
                        type: "int",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "asc",
                        groupable: "False",
                        cellTemp: "default-cell",
                        headerComponent: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 70,
                    },
                    {
                        name: "Url",
                        title: "Destination URL",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "keyword-analysis-url",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 150,
                    },
                ],
                filters: {
                    orderBy: [
                        {
                            value: "TotalShare desc",
                            title: "Traffic Share",
                        },
                    ],
                },
            },
            KeywordsGraph: {
                properties: {
                    stacked: true,
                    options: {
                        showMarkersOnSingle: true,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Visits",
                    type: "long",
                    format: "percentagesign",
                    formatParameter: 2,
                    name: "Visits",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {},
            },
            KeywordsGraphDashboard: {
                properties: {
                    stacked: true,
                    showMoreButton: true,
                    apiParams: {
                        pageSize: "10",
                    },
                    options: {
                        showMarkersOnSingle: true,
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Visits",
                    type: "long",
                    format: "percentagesign",
                    formatParameter: 2,
                    name: "Visits",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {},
            },
        },
        compare: {},
    },
};

/**
 * @deprecated KeywordAnalysisTotal --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisTotal = {
    ...KeywordAnalysisOrganic,
    id: "KeywordAnalysisTotal",
    properties: {
        component: "KeywordAnalysisTotal",
        family: "Keyword",
        dashboard: "true",
        title: "KeywordAnalysis.total.traffic.title",
        viewPermissions: {
            trafficSources: ["Total"],
        },
        hasWebSource: true,
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsTotalKeywords",
        isHidden: stubTrue,
        isDeprecated: true,
    },
};

/**
 * @deprecated KeywordAnalysisGroupOrganic --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisGroupOrganic = {
    id: "KeywordAnalysisGroupOrganic",
    properties: {
        component: "KeywordAnalysisOP",
        family: "Keyword",
        dashboard: "true",
        title: "keywordAnalysis.organic.group.breakdown",
        viewPermissions: {
            trafficSources: ["MobileWeb", "Desktop"],
        },
        hasWebSource: true,
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsOverviewKeywords",
        isHidden: stubTrue,
        isDeprecated: true,
    },
    widgets: {
        single: {
            defaultType: "KeywordsDashboardTable",
            KeywordsDashboardTable: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        showFrame: true,
                        showLegend: false,
                        showOverflow: true,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                columns: [
                    {
                        name: "Keyword",
                        title: "Keyword",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "cell-keyword-dashboard",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 100,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 50,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },

                    {
                        name: "Cpc",
                        title: "CPC",
                        trackingName: "CPC",
                        displayName: "CPC",
                        tooltip: true,
                        headerComponent: DefaultCellHeaderRightAlign,
                        cellComponent: DefaultCellRightAlign,
                        format: "CPC",
                        sortable: true,
                        minWidth: 50,
                    },
                    {
                        name: "Volume",
                        title: "Volume",
                        displayName: "Volume",
                        tooltip: true,
                        cellTemplate: "default-cell",
                        trackingName: "Volume",
                        format: "abbrNumber",
                        sortable: true,
                        sortDirection: "desc",
                        headerComponent: DefaultCellHeaderRightAlign,
                        cellComponent: DefaultCellRightAlign,
                        minWidth: 50,
                    },
                ],
                filters: {
                    orderBy: [
                        {
                            value: "Share desc",
                            title: "Traffic Share",
                        },
                    ],
                },
            },
            KeywordsTable: {
                properties: {},
                columns: [
                    {
                        fixed: true,
                        cellTemp: "index",
                        sortable: false,
                        width: 65,
                        disableHeaderCellHover: true,
                    },
                    {
                        name: "Keyword",
                        title: "Keyword",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "keywordcell",
                        cellComponent: "keywordcell",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 150,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },

                    {
                        name: "Cpc",
                        title: "CPC",
                        trackingName: "CPC",
                        displayName: "CPC",
                        tooltip: true,
                        headerComponent: DefaultCellHeaderRightAlign,
                        cellComponent: DefaultCellRightAlign,
                        format: "CPC",
                        sortable: true,
                        minWidth: 150,
                    },
                    {
                        name: "Volume",
                        title: "Volume",
                        displayName: "Volume",
                        tooltip: true,
                        cellTemplate: "default-cell",
                        trackingName: "Volume",
                        format: "abbrNumber",
                        sortable: true,
                        sortDirection: "desc",
                        minWidth: 150,
                    },
                ],
                filters: {},
            },
        },
        compare: {},
    },
};

/**
 * @deprecated KeywordAnalysisGroupPaid --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisGroupPaid = {
    id: "KeywordAnalysisGroupPaid",
    properties: {
        component: "KeywordAnalysisOP",
        family: "Keyword",
        dashboard: "true",
        title: "keywordAnalysis.paid.group.breakdown",
        viewPermissions: {
            trafficSources: ["Desktop"],
        },
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsPaidKeywords",
        isHidden: stubTrue,
        isDeprecated: true,
    },
    widgets: {
        single: {
            defaultType: "KeywordsDashboardTable",
            KeywordsTable: {
                properties: {},
                columns: [
                    {
                        fixed: true,
                        cellTemp: "index",
                        sortable: false,
                        width: 65,
                        disableHeaderCellHover: true,
                    },
                    {
                        name: "Keyword",
                        title: "Keyword",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "keywordcell",
                        cellComponent: "keywordcell",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 150,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },

                    {
                        name: "Cpc",
                        title: "CPC",
                        trackingName: "CPC",
                        displayName: "CPC",
                        tooltip: true,
                        headerComponent: DefaultCellHeaderRightAlign,
                        cellComponent: DefaultCellRightAlign,
                        format: "CPC",
                        sortable: true,
                        minWidth: 150,
                    },
                    {
                        name: "Volume",
                        title: "Volume",
                        displayName: "Volume",
                        tooltip: true,
                        cellTemplate: "default-cell",
                        trackingName: "Volume",
                        format: "abbrNumber",
                        sortable: true,
                        sortDirection: "desc",
                        minWidth: 150,
                    },
                ],
                filters: {},
            },
            KeywordsDashboardTable: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        showFrame: true,
                        showLegend: false,
                        showOverflow: true,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                columns: [
                    {
                        name: "Keyword",
                        title: "Keyword",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "cell-keyword-dashboard",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 100,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 50,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },

                    {
                        name: "Cpc",
                        title: "CPC",
                        trackingName: "CPC",
                        displayName: "CPC",
                        tooltip: true,
                        headerComponent: DefaultCellHeaderRightAlign,
                        cellComponent: DefaultCellRightAlign,
                        format: "CPC",
                        sortable: true,
                        minWidth: 50,
                    },
                    {
                        name: "Volume",
                        title: "Volume",
                        displayName: "Volume",
                        tooltip: true,
                        cellTemplate: "default-cell",
                        trackingName: "Volume",
                        format: "abbrNumber",
                        sortable: true,
                        sortDirection: "desc",
                        headerComponent: DefaultCellHeaderRightAlign,
                        cellComponent: DefaultCellRightAlign,
                        minWidth: 50,
                    },
                ],
                filters: {},
            },
        },
        compare: {},
    },
};

export const KeywordAnalysisGroupTotal = {
    id: "KeywordAnalysisGroupTotal",
    properties: {
        component: "KeywordAnalysisTotal",
        family: "Keyword",
        dashboard: "true",
        state: "keywordAnalysis-total",
        title: "keywordAnalysis.total.group.breakdown",
        viewPermissions: {
            trafficSources: ["Total"],
        },
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "DigitalSuiteAcquisition",
        isHidden: stubTrue,
    },
    widgets: {
        single: {
            defaultType: "KeywordsDashboardTable",
            KeywordsTable: {
                properties: {},
                columns: [
                    {
                        fixed: true,
                        cellTemp: "index",
                        sortable: false,
                        width: 65,
                        disableHeaderCellHover: true,
                    },
                    {
                        name: "Keyword",
                        title: "Keyword",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "keywordcell",
                        cellComponent: "keywordcell",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 150,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },
                    {
                        name: "Volume",
                        title: "Volume",
                        displayName: "Volume",
                        tooltip: true,
                        cellTemplate: "default-cell",
                        trackingName: "Volume",
                        format: "abbrNumber",
                        sortable: true,
                        sortDirection: "desc",
                        minWidth: 150,
                    },
                ],
                filters: {},
            },
            KeywordsDashboardTable: {
                properties: {
                    showMoreButtonItems: 5,
                    options: {
                        showFrame: true,
                        showLegend: false,
                        showOverflow: true,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                columns: [
                    {
                        name: "Keyword",
                        title: "Keyword",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "cell-keyword-dashboard",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        minWidth: 300,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 100,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 50,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },
                    {
                        name: "Volume",
                        title: "Volume",
                        displayName: "Volume",
                        tooltip: true,
                        cellTemplate: "default-cell",
                        trackingName: "Volume",
                        format: "abbrNumber",
                        sortable: true,
                        sortDirection: "desc",
                        headerComponent: DefaultCellHeaderRightAlign,
                        cellComponent: DefaultCellRightAlign,
                        minWidth: 50,
                    },
                ],
                filters: {},
            },
        },
        compare: {},
    },
};

/**
 * @deprecated KeywordAnalysisPaid --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisPaid = {
    id: "KeywordAnalysisPaid",
    properties: {
        component: "KeywordAnalysisOP",
        dashboard: "true",
        title: "KeywordAnalysis.paid.traffic.title",
        family: "Keyword",
        webSource: "Desktop",
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsPaidKeywords",
        isHidden: stubTrue,
        isDeprecated: true,
    },
    widgets: {
        single: {
            KeywordsTable: {
                properties: {},
                columns: [
                    {
                        fixed: true,
                        name: "row-selection",
                        cellTemp: "row-selection",
                        sortable: false,
                        width: 33,
                        disableHeaderCellHover: true,
                    },
                    {
                        fixed: true,
                        cellTemp: "index",
                        sortable: false,
                        width: 65,
                        disableHeaderCellHover: true,
                    },
                    {
                        name: "Domain",
                        fixed: true,
                        title: "Domain",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "True",
                        cellTemp: "keyword-analysis-traffic-source",
                        headTemp: "grouped-header-cell",
                        totalCount: "True",
                        tooltip: true,
                        width: 220,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        width: 275,
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "change-percentage",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        width: 110,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },
                    {
                        name: "Position",
                        title: "Position",
                        type: "int",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "asc",
                        groupable: "False",
                        cellTemp: "keyword-analysis-position",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        width: 90,
                    },
                    {
                        name: "Url",
                        title: "Destination URL",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "keyword-analysis-url",
                        cellCls: "url-cell",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        width: 275,
                    },
                    {
                        name: "Category",
                        title: "dropdown.category",
                        type: "string",
                        format: "prettifyCategory",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "default-cell",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 220,
                    },
                ],
                filters: {},
            },
            KeywordsDashboardTable: {
                properties: {
                    apiParams: {
                        pageSize: "10",
                    },
                    showMoreButtonItems: 5,
                    options: {
                        showFrame: true,
                        showLegend: false,
                        showOverflow: true,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                columns: [
                    {
                        name: "Domain",
                        fixed: true,
                        title: "Domain",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "website-tooltip-top-no-expand-cell",
                        headTemp: "grouped-header-cell",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 220,
                    },
                    {
                        name: "Share",
                        title: "Traffic Share",
                        type: "string",
                        format: "percentagesign",
                        sortable: "False",
                        isSorted: "True",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "traffic-share-dashboard",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        width: "115px",
                    },
                    {
                        name: "Change",
                        title: "Change",
                        type: "double",
                        format: "percentagesign",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "Change-percentage-dashboard",
                        headerComponent: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 110,
                        ppt: {
                            overrideFormat: "smallNumbersPercentage:2",
                        },
                    },
                    {
                        name: "Position",
                        title: "Pos.",
                        type: "int",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "asc",
                        groupable: "False",
                        cellTemp: "default-cell",
                        headerComponent: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 70,
                    },
                    {
                        name: "Url",
                        title: "Destination URL",
                        type: "string",
                        format: "None",
                        sortable: "True",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "keyword-analysis-url",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 150,
                    },
                ],
                filters: {
                    orderBy: [
                        {
                            value: "TotalShare desc",
                            title: "Traffic Share",
                        },
                    ],
                },
            },
            KeywordsGraph: {
                properties: {
                    hideMarkersOnDaily: false,
                    stacked: true,
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Visits",
                    type: "long",
                    format: "percentagesign",
                    formatParameter: 2,
                    name: "Visits",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {},
            },
            KeywordsGraphDashboard: {
                properties: {
                    hideMarkersOnDaily: false,
                    showMoreButton: true,
                    stacked: true,
                    apiParams: {
                        pageSize: "10",
                    },
                },
                x_axis: {
                    title: "Date",
                    type: "date",
                    format: "None",
                    name: "Date",
                    reversed: "False",
                },
                y_axis: {
                    title: "Visits",
                    type: "long",
                    format: "percentagesign",
                    formatParameter: 2,
                    name: "Visits",
                    reversed: "False",
                    yAxisMax: 1,
                },
                filters: {},
            },
        },
        compare: {},
    },
};

/**
 * @deprecated KeywordAnalysisTrafficShare --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisTrafficShare = {
    id: "KeywordAnalysisTrafficShare",
    properties: {
        component: "KeywordAnalysisOP",
        family: "Keyword",
        dashboard: "true",
        title: "keywordAnalysis.organic.widgets.trafficshare",
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsWebSearchCompetitors",
        isHidden: stubTrue,
        isDeprecated: true,
    },
    widgets: {
        single: {
            PieChart: {
                properties: {
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                objects: {
                    Organic: {
                        name: "Organic",
                        title: "Organic",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                    Paid: {
                        name: "Paid",
                        title: "Paid",
                        type: "double",
                        format: "None",
                        cellTemp: "",
                    },
                },
            },
        },
        compare: {},
    },
};

/**
 * @deprecated KeywordAnalysisTrafficShareOverTime --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisTrafficShareOverTime = {
    id: "KeywordAnalysisTrafficShareOverTime",
    properties: {
        component: "KeywordAnalysisOPTotal",
        family: "Keyword",
        dashboard: "true",
        title: "keywordAnalysis.organic.widgets.totalsearchvisits",
        apiController: "KeywordAnalysisOP",
        disableWindowInDatepicker: false,
        availabilityComponent: "WsWebSearchCompetitors",
        isHidden: stubTrue,
        isDeprecated: true,
    },
    widgets: {
        single: {
            defaultType: "KeywordAnalysisTotalVisits",
            KeywordAnalysisTotalVisits: {
                properties: {
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
            },
        },
        compare: {},
    },
};

/**
 * @deprecated KeywordAnalysisVolumes --
 * This metric config has been deprecated and is no longer supported in the dashboards.
 */
export const KeywordAnalysisVolumes = {
    id: "KeywordAnalysisVolumes",
    properties: {
        component: "KeywordAnalysisOPTotal",
        family: "Keyword",
        dashboard: "true",
        title: "KeywordAnalysis.organic.widgets.searches",
        webSource: "Desktop",
        subtitle: "KeywordAnalysis.organic.searchVolume.subtitle",
        apiController: "KeywordAnalysisOP",
        availabilityComponent: "WsPaidKeywords",
        isHidden: stubTrue,
        isDeprecated: true,
    },
    widgets: {
        single: {
            SingleMetric: {
                properties: {
                    apiController: "KeywordAnalysisOP",
                    height: 82,
                    loadingHeight: "87px",
                    family: "Keyword",
                    options: {
                        showTitleTooltip: false,
                        showOverflow: true,
                        showLegend: false,
                        showSettings: true,
                        showTopLine: false,
                        titleIcon: false,
                        template: "/app/pages/keyword-analysis/templates/searches-dashboard.html",
                    },
                },
                objects: {
                    Volume: {
                        name: "Volume",
                        title: "",
                        type: "double",
                        format: "abbrNumber",
                        cellTemp: "large-number",
                    },
                    VolumesMonthly: {
                        name: "VolumesMonthly",
                        title: "",
                        type: "long[]",
                        format: "None",
                        cellTemp: "large-number",
                        ppt: {
                            ignoreRow: true,
                        },
                    },
                },
            },
        },
        compare: {},
    },
};

const getKeywordOverviewStatsMetric = (id, title, objects) => ({
    id,
    properties: {
        family: "Keyword",
        dashboard: "true",
        title,
        component: "KeywordAnalysisV2Overview",
        mobileWebComponent: "KeywordAnalysisV2Overview",
        availabilityComponent: "DigitalSuiteAcquisition",
        state: "organicsearch_keywordAnalysis_overview_2",
        viewPermissions: {
            trafficSources: [devicesTypes.TOTAL, devicesTypes.DESKTOP, devicesTypes.MOBILE],
        },
        hasWebSource: true,
    },
    widgets: {
        single: {
            defaultType: "KeywordOverviewStats",
            KeywordOverviewStats: {
                properties: {
                    options: {
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                },
                objects,
            },
        },
        compare: {},
    },
});

const volumeRowConfig = voulmeClicksRowsConfig.find((row) => row.id === EVolumeAndClicksMetric.VOLUME);
const clicksRowConfig = voulmeClicksRowsConfig.find((row) => row.id === EVolumeAndClicksMetric.CLICKS);
const zeroClicksRowConfig = voulmeClicksRowsConfig.find((row) => row.id === EVolumeAndClicksMetric.ZERO_CLICKS);
const organicSerpRowConfig = serpRowsConfigs.find((row) => row.id === ESerpMetric.ORGANIC);
const paidSerpRowConfig = serpRowsConfigs.find((row) => row.id === ESerpMetric.PAID);
const cpcSerpRowConfig = serpRowsConfigs.find((row) => row.id === ESerpMetric.CPC);

export const KeywordOverviewPerformanceStats = getKeywordOverviewStatsMetric(
    "KeywordOverviewPerformanceStats",
    "keyword_research.overview.widgets.performance",
    {
        volume: {
            name: "volumeRowData",
            get title() {
                return translate(volumeRowConfig?.title ?? "");
            },
            get tooltip() {
                return translate(volumeRowConfig?.tooltip ?? "");
            },
            filter: volumeRowConfig?.filter ?? (() => ""),
            headTemp: "none",
            cellTemp: "number-with-change",
            rowClass: "keyword-overview-stats",
        },
        clicks: {
            name: "clicksRowData",
            get title() {
                return translate(clicksRowConfig?.title ?? "");
            },
            get tooltip() {
                return translate(clicksRowConfig?.tooltip ?? "");
            },
            filter: clicksRowConfig?.filter ?? (() => ""),
            headTemp: "none",
            cellTemp: "number-with-change",
            rowClass: "keyword-overview-stats",
        },
        zeroClicks: {
            name: "zeroClicksRowData",
            get title() {
                return translate(zeroClicksRowConfig?.title ?? "");
            },
            get tooltip() {
                return translate(zeroClicksRowConfig?.tooltip ?? "");
            },
            filter: zeroClicksRowConfig?.filter ?? (() => ""),
            headTemp: "none",
            cellTemp: "number-with-change",
            rowClass: "keyword-overview-stats",
            displayNoData: true,
            invertChangeColor: true,
        },
    },
);

export const KeywordOverviewCompetitiveStats = getKeywordOverviewStatsMetric(
    "KeywordOverviewCompetitiveStats",
    "keyword_research.overview.widgets.competitive",
    {
        organicDifficulty: {
            name: "organicRowData",
            get title() {
                return translate(organicSerpRowConfig?.title ?? "");
            },
            get tooltip() {
                return translate(organicSerpRowConfig?.tooltip ?? "");
            },
            displayNoData: true,
            cellTemp: "simple-value",
            headTemp: "none",
            rowClass: "keyword-overview-stats",
        },
        paidCompetition: {
            name: "paidRowData",
            get title() {
                return translate(paidSerpRowConfig?.title ?? "");
            },
            get tooltip() {
                return translate(paidSerpRowConfig?.tooltip ?? "");
            },
            displayNoData: true,
            cellTemp: "simple-value",
            headTemp: "none",
            rowClass: "keyword-overview-stats",
        },
        cPCRange: {
            name: "cpcRowData",
            get title() {
                return translate(cpcSerpRowConfig?.title ?? "");
            },
            get tooltip() {
                return translate(cpcSerpRowConfig?.tooltip ?? "");
            },
            displayNoData: true,
            cellTemp: "simple-value",
            headTemp: "none",
            rowClass: "keyword-overview-stats",
        },
    },
);
